import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useBlogContent } from "../hooks/useBlogContent";

function BlogPage() {
  const { getAllPosts } = useBlogContent();
  const blogPosts = getAllPosts();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Blog Yazıları | Psikolog Yağmur Demiröz</title>
        <meta 
          name="description" 
          content="Psikoloji, oyun terapisi, MOXO testi, öğrenci danışmanlığı ve ruh sağlığına dair en güncel blog yazılarını keşfedin. Bilimsel içeriklerle ruh sağlığınızı destekleyin." 
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://yagmurdemiroz.com/blog" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yagmurdemiroz.com/blog" />
        <meta property="og:title" content="Blog Yazıları | Psikolog Yağmur Demiröz" />
        <meta property="og:description" content="Psikoloji, oyun terapisi, MOXO testi, öğrenci danışmanlığı ve ruh sağlığına dair en güncel blog yazılarını keşfedin. Bilimsel içeriklerle ruh sağlığınızı destekleyin." />
        <meta property="og:image" content="https://yagmurdemiroz.com/me.jpg" />
        <meta property="og:locale" content="tr_TR" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://yagmurdemiroz.com/blog" />
        <meta name="twitter:title" content="Blog Yazıları | Psikolog Yağmur Demiröz" />
        <meta name="twitter:description" content="Psikoloji, oyun terapisi, MOXO testi, öğrenci danışmanlığı ve ruh sağlığına dair en güncel blog yazılarını keşfedin. Bilimsel içeriklerle ruh sağlığınızı destekleyin." />
        <meta name="twitter:image" content="https://yagmurdemiroz.com/me.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Psikoloji ve Danışmanlık Blogu",
            "publisher": {
              "@type": "Organization",
              "name": "Psikolog Yağmur Demiröz"
            },
            "blogPosts": blogPosts.map(post => ({
              "@type": "BlogPosting",
              "headline": post.title,
              "datePublished": post.date,
              "description": post.excerpt,
              "url": `https://yagmurdemiroz.com/blog/${post.slug}`,
              "image": post.image?.startsWith('http') ? post.image : `https://yagmurdemiroz.com${post.image}`
            }))
          })}
        </script>
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <header className="text-center mb-8">
          <div className="animate-fadeIn">
            <h1 className="text-4xl font-bold text-secondary">Blog Yazıları</h1>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post, index) => (
            <div 
              key={post.id} 
              className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100"
              style={{
                animation: 'fadeInUp 0.5s ease-in-out forwards',
                opacity: 0,
                animationDelay: `${index * 0.2}s`
              }}
            >
              <img 
                src={post.image} 
                alt={post.title} 
                className="w-full h-48 object-cover"
                loading="lazy"
                style={{
                  animation: 'scaleIn 0.5s ease-in-out forwards',
                  opacity: 0,
                  animationDelay: `${index * 0.2 + 0.2}s`
                }}
              />
              <div className="p-6">
                <p 
                  className="text-sm text-secondary mb-2 font-medium"
                  style={{
                    animation: 'fadeIn 0.5s ease-in-out forwards',
                    opacity: 0,
                    animationDelay: `${index * 0.2 + 0.3}s`
                  }}
                >
                  {post.date}
                </p>
                <h2 
                  className="text-xl font-semibold text-secondary mb-3"
                  style={{
                    animation: 'fadeIn 0.5s ease-in-out forwards',
                    opacity: 0,
                    animationDelay: `${index * 0.2 + 0.4}s`
                  }}
                >
                  <Link to={`/blog/${post.slug}`} className="hover:underline">
                    {post.title}
                  </Link>
                </h2>
                <p 
                  className="text-gray-600 mb-4"
                  style={{
                    animation: 'fadeIn 0.5s ease-in-out forwards',
                    opacity: 0,
                    animationDelay: `${index * 0.2 + 0.5}s`
                  }}
                >
                  {post.excerpt}
                </p>
                <Link
                  to={`/blog/${post.slug}`}
                  className="blog-card-button inline-block bg-secondary text-white py-2 px-4 rounded-md hover:bg-primary hover:text-secondary border border-secondary transition-all duration-300 font-medium"
                  style={{
                    animation: 'fadeInUp 0.5s ease-in-out forwards',
                    opacity: 0,
                    animationDelay: `${index * 0.2 + 0.6}s`
                  }}
                >
                  Devamını Oku
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div 
          className="text-center mt-10"
          style={{
            animation: 'fadeInUp 0.5s ease-in-out forwards',
            opacity: 0,
            animationDelay: '0.8s'
          }}
        >
          <p className="text-gray-700">
            Daha fazla bilgi için <Link to="/iletisim" className="text-secondary font-semibold hover:underline">İletişim</Link> sayfasını ziyaret edebilirsiniz. 
          </p>
        </div>
      </div>
    </div>
  );
}

export default BlogPage;
